import type {
  FileResponse,
  PositionResponse,
  RunGateResponse,
} from "@patent-studio/api-types";
import { useNoveltyGate } from "@/hooks/use-novelty-gate";
import { StageFooter, type FooterCondition } from "../layout/StageFooter";
import { priorArtTypeId } from "./anchors";
import { Spinner } from "@/components/ui/Spinner";
import { Unanswered } from "./Unanswered";
import { CHECKING_FOR_QUESTIONS } from "../questions/useQuestionGate";

/** Where a condition's first outstanding item lives on the page, as an element id. */
function gateTargetId(condition: FooterCondition): string | null {
  // Only the typed condition names a document; the positions are on their own tab.
  return condition.id === "typed" && condition.first_id !== null
    ? priorArtTypeId(condition.first_id)
    : null;
}

/** Until the server has answered, the gate is closed and names nothing. */
const UNANSWERED: RunGateResponse = {
  ready: false,
  conditions: [],
  analysis: { status: "not_started", stale: false, run_id: null },
  commit: { ready: false, outstanding: 0, first_id: null },
};

/** What the footer leads with, from the one state that answers first. */
function heading({
  unanswerable,
  running,
  stopped,
  noArt,
}: {
  unanswerable: boolean;
  running: boolean;
  stopped: string | null;
  noArt: boolean;
}): string {
  if (unanswerable) return "The gate could not be checked";
  if (running) return "Running the novelty analysis";
  if (stopped !== null) return "The analysis stopped before it finished";
  if (noArt) return "Upload prior art to continue";
  return "Run the analysis to see what is novel";
}

/** The line under the heading: what the run is waiting on, or doing. */
function detail({
  running,
  noArt,
  documents,
}: {
  running: boolean;
  noArt: boolean;
  documents: number;
}): string {
  if (running) return `Checking every position against ${documents} prior art`;
  if (noArt) {
    return "Novelty can only be assessed against prior art. Add at least one document above.";
  }
  return "Nothing can be confirmed until the positions have been assessed against your prior art.";
}

/**
 * The gate out of the tab, as the server projects it. The run stays disabled
 * until the library is readable and typed and there is a position to assess;
 * while it is, the footer names each outstanding condition and takes the
 * attorney to the first item. A gate the server could not answer for stays
 * closed too, but says so and offers to ask again, rather than telling the
 * attorney to settle inputs that may already be settled. The files and
 * positions are handed in only so the gate is fetched again the moment the
 * screen knows they moved.
 */
export function RunFooter({
  taskId,
  files,
  positions,
  onRun,
  starting = false,
  asking = false,
  refusal = null,
}: {
  taskId: string;
  files: readonly FileResponse[];
  positions: readonly PositionResponse[];
  onRun: () => void;
  /** A start is in flight. */
  starting?: boolean;
  /** The run is asking whether Praxis has questions first. */
  asking?: boolean;
  /** What the server said when it refused the last start, or nothing. */
  refusal?: string | null;
}) {
  const { data, isError, error, refetch } = useNoveltyGate(
    taskId,
    files,
    positions,
  );
  const gate = data ?? UNANSWERED;
  // A run in flight closes the gate the same way a start does: the server
  // refuses a second start, and the button says why rather than arguing.
  const running = gate.analysis.status === "running";
  // A run the worker abandoned is failed with a reason on it, and nothing else says so:
  // without this the attorney reads only that nothing is running, beside a button that
  // looks like a first run rather than another go at one that stopped.
  const stopped =
    gate.analysis.status === "failed" ? (gate.analysis.error ?? null) : null;
  // A refusal names a moment. Once the gate has been read again and is open
  // with no run in flight, the moment has passed and the words come down.
  const refused = refusal !== null && !(gate.ready && !running);
  // The library is empty, not merely unread: a document being read is in the list above.
  const noArt = files.length === 0;
  // Once a completed run is current the positions are assessed, and the work moves to the analysis.
  if (gate.analysis.status === "ready" && !gate.analysis.stale) return null;
  return (
    <StageFooter
      testId="novelty-run-footer"
      heading={heading({
        unanswerable: isError,
        running,
        stopped,
        noArt,
      })}
      detail={detail({ running, noArt, documents: files.length })}
      ready={gate.ready}
      submitting={starting || asking || running}
      label={
        gate.analysis.stale ? "Re-run novelty analysis" : "Run novelty analysis"
      }
      pendingLabel={asking ? CHECKING_FOR_QUESTIONS : "Analysing…"}
      onSubmit={onRun}
      sticky
      conditions={isError ? [] : gate.conditions}
      resolveTarget={gateTargetId}
    >
      {running && (
        <span data-testid="run-footer-spinner" className="mt-1 inline-flex">
          <Spinner size="sm" />
        </span>
      )}
      {isError && (
        <Unanswered
          what="Whether the analysis can run"
          error={error}
          onRetry={() => void refetch()}
        />
      )}
      {stopped !== null && (
        <p role="alert" className="mt-2 text-[11.5px] text-destructive">
          {stopped} Run it again when you are ready.
        </p>
      )}
      {refused && (
        <p role="alert" className="mt-2 text-[11.5px] text-destructive">
          {refusal}
        </p>
      )}
    </StageFooter>
  );
}
